import { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

interface ContextDocument {
  id: string;
  path: string;
  category: 'example' | 'capability' | 'template' | 'imported';
  metadata: {
    title: string;
    tags: string[];
    summary: string;
    folderPath?: string;
    modifiedTime?: string;
  };
}

interface DocumentManagerProps {
  documents: ContextDocument[];
  onDocumentDeleted: () => void;
}

type SortOption = 'name' | 'date' | 'folder';

interface FolderNode {
  name: string;
  path: string;
  children: FolderNode[];
  documents: ContextDocument[];
}

export default function DocumentManager({ documents, onDocumentDeleted }: DocumentManagerProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<ContextDocument | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<FolderNode | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('folder');
  const [expanded, setExpanded] = useState<string[]>([]);

  // Strip technical folders (google-drive/{userId}) from display
  const normalizeDisplayPath = (folderPath: string | undefined): string => {
    if (!folderPath) return '';

    const parts = folderPath.split('/').filter(Boolean);

    // Remove 'google-drive' and user ID (first 2 segments) if present
    if (parts.length >= 2 && parts[0] === 'google-drive') {
      return parts.slice(2).join('/');
    }

    return folderPath;
  };

  const buildFolderTree = (): FolderNode => {
    const root: FolderNode = {
      name: 'Root',
      path: '',
      children: [],
      documents: [],
    };

    const folderMap = new Map<string, FolderNode>();
    folderMap.set('', root);

    // Sort documents based on selected option
    const sortedDocs = [...documents].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.metadata.title.localeCompare(b.metadata.title);
        case 'date':
          return (b.metadata.modifiedTime || '').localeCompare(a.metadata.modifiedTime || '');
        case 'folder':
        default:
          return (a.metadata.folderPath || '').localeCompare(b.metadata.folderPath || '');
      }
    });

    sortedDocs.forEach((doc) => {
      // Normalize the folder path for display
      const displayPath = normalizeDisplayPath(doc.metadata.folderPath);

      if (!displayPath) {
        root.documents.push(doc);
        return;
      }

      // Create folder structure
      const parts = displayPath.split('/').filter(Boolean);
      let currentPath = '';

      parts.forEach((part, index) => {
        const parentPath = currentPath;
        currentPath = currentPath ? `${currentPath}/${part}` : part;

        if (!folderMap.has(currentPath)) {
          const newFolder: FolderNode = {
            name: part,
            path: currentPath,
            children: [],
            documents: [],
          };

          folderMap.set(currentPath, newFolder);

          const parent = folderMap.get(parentPath)!;
          parent.children.push(newFolder);
        }

        // Add document to the deepest folder
        if (index === parts.length - 1) {
          const folder = folderMap.get(currentPath)!;
          folder.documents.push(doc);
        }
      });
    });

    return root;
  };

  const handleDeleteClick = (doc: ContextDocument) => {
    setDocumentToDelete(doc);
    setFolderToDelete(null);
    setDeleteDialogOpen(true);
  };

  const handleFolderDeleteClick = (folder: FolderNode) => {
    setFolderToDelete(folder);
    setDocumentToDelete(null);
    setDeleteDialogOpen(true);
  };

  const countFolderDocuments = (folder: FolderNode): number => {
    let count = folder.documents.length;
    folder.children.forEach(child => {
      count += countFolderDocuments(child);
    });
    return count;
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete && !folderToDelete) return;

    try {
      setDeleting(true);

      if (documentToDelete) {
        await axios.delete(API_ENDPOINTS.context.delete(documentToDelete.id), {
          withCredentials: true,
        });
      } else if (folderToDelete) {
        // Encode each path segment separately to preserve slashes
        const encodedPath = folderToDelete.path.split('/').map(encodeURIComponent).join('/');
        await axios.delete(API_ENDPOINTS.context.deleteFolder(encodedPath), {
          withCredentials: true,
        });
      }

      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
      setFolderToDelete(null);
      onDocumentDeleted();
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const renderFolderNode = (node: FolderNode): React.JSX.Element => {
    const itemId = node.path || 'root';
    const totalDocs = countFolderDocuments(node);
    const hasChildren = node.children.length > 0;

    return (
      <TreeItem
        key={itemId}
        itemId={itemId}
        label={
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            py={0.5}
            sx={{
              '&:hover .delete-button': {
                opacity: 1,
              },
            }}
          >
            <FolderIcon fontSize="small" color="primary" />
            <Typography fontWeight={500}>{node.name}</Typography>
            {totalDocs > 0 && (
              <Chip label={`${totalDocs} docs`} size="small" variant="outlined" />
            )}
            <Box sx={{ flex: 1 }} />
            {node.path && (
              <Tooltip title="Delete folder and all contents">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFolderDeleteClick(node);
                  }}
                  className="delete-button"
                  sx={{ opacity: 0.3, transition: 'opacity 0.2s' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        }
      >
        {node.documents.map((doc) => (
          <TreeItem
            key={doc.id}
            itemId={doc.id}
            label={
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                py={0.5}
                sx={{
                  '&:hover .delete-button': {
                    opacity: 1,
                  },
                }}
              >
                <DescriptionIcon fontSize="small" color="action" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">{doc.metadata.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(doc.metadata.modifiedTime)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  {doc.metadata.tags.slice(0, 2).map((tag, idx) => (
                    <Chip key={idx} label={tag} size="small" variant="outlined" sx={{ height: 20 }} />
                  ))}
                  {doc.metadata.tags.length > 2 && (
                    <Chip
                      label={`+${doc.metadata.tags.length - 2}`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20 }}
                    />
                  )}
                </Stack>
                <Tooltip title="Delete document">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(doc);
                    }}
                    className="delete-button"
                    sx={{ opacity: 0.3, transition: 'opacity 0.2s' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            }
          />
        ))}
        {hasChildren && node.children.map(renderFolderNode)}
      </TreeItem>
    );
  };

  const folderTree = buildFolderTree();

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Imported Documents ({documents.length})
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort by</InputLabel>
          <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value as SortOption)}>
            <MenuItem value="folder">Folder</MenuItem>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="date">Date</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {documents.length === 0 ? (
        <Alert severity="info">No documents imported yet</Alert>
      ) : (
        <SimpleTreeView
          expandedItems={expanded}
          onExpandedItemsChange={(_event: React.SyntheticEvent | null, nodeIds: string[]) => setExpanded(nodeIds)}
          slots={{
            collapseIcon: ExpandMoreIcon,
            expandIcon: ChevronRightIcon,
          }}
        >
          {folderTree.documents.length > 0 && (
            <TreeItem
              itemId="root-docs"
              label={
                <Stack direction="row" alignItems="center" spacing={1} py={0.5}>
                  <FolderIcon fontSize="small" color="primary" />
                  <Typography fontWeight={500}>Root</Typography>
                  <Chip label={`${folderTree.documents.length} docs`} size="small" variant="outlined" />
                </Stack>
              }
            >
              {folderTree.documents.map((doc) => (
                <TreeItem
                  key={doc.id}
                  itemId={doc.id}
                  label={
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      py={0.5}
                      sx={{
                        '&:hover .delete-button': {
                          opacity: 1,
                        },
                      }}
                    >
                      <DescriptionIcon fontSize="small" color="action" />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">{doc.metadata.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(doc.metadata.modifiedTime)}
                        </Typography>
                      </Box>
                      <Tooltip title="Delete document">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(doc);
                          }}
                          className="delete-button"
                          sx={{ opacity: 0.3, transition: 'opacity 0.2s' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  }
                />
              ))}
            </TreeItem>
          )}
          {folderTree.children.map(renderFolderNode)}
        </SimpleTreeView>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          {documentToDelete ? 'Delete Document' : 'Delete Folder'}
        </DialogTitle>
        <DialogContent>
          {documentToDelete && (
            <Typography>
              Are you sure you want to delete "{documentToDelete.metadata.title}"? This action cannot be undone.
            </Typography>
          )}
          {folderToDelete && (
            <>
              <Typography gutterBottom>
                Are you sure you want to delete the folder "{folderToDelete.name}" and all its contents?
              </Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>
                This will permanently delete {countFolderDocuments(folderToDelete)} document(s). This action cannot be undone.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
